import type { ReactNode } from 'react';

/**
 * Esqueleto (v2.1, p.32): carregando e a FORMA do conteudo, no lugar exato em
 * que o conteudo vai chegar, pulsando devagar.
 *
 * Tres regras que valem para tudo aqui:
 *
 *  - NUNCA RODINHA, NUNCA "CARREGANDO…" SOLTO, NUNCA "—" NO LUGAR DO VALOR. A
 *    rodinha e a frase nao reservam espaco: o dado chega, a tela pula, e quem
 *    ja estava indo clicar clica em outra coisa. O "—" e pior: parece um valor;
 *  - O QUE JA SE SABE APARECE. Rotulo de card, cabecalho de tabela, titulo de
 *    secao e nome de aba nao dependem da resposta — so o dado e esqueleto;
 *  - QUEM LE A TELA OUVE O QUE ESTA VINDO, uma vez ("Carregando a frota",
 *    p.60). As barras sao decorativas e ficam fora da arvore de acessibilidade;
 *    a frase mora no `Carregando`, que e uma regiao de status.
 *
 * Toda medida e multiplo de 4 px (regra 16): barra de 12 px para a linha de
 * texto, 8 px para a legenda, 32 para o valor do indicador, 40 para o campo.
 */

/** Uma barra. Decorativa: quem diz o que esta carregando e o `Carregando`. */
export function Barra({ className = '' }: { readonly className?: string }) {
  return <span aria-hidden="true" className={`esqueleto block shrink-0 ${className}`} />;
}

/**
 * A regiao que carrega. Anuncia ao leitor de tela o que esta vindo — a frase
 * do manual, "Carregando a frota", e nao "Aguarde..." — e marca a regiao como
 * ocupada. O desenho e de quem chama.
 */
export function Carregando({
  oQue,
  children,
  className = '',
}: {
  /** A frase inteira: "Carregando os tanques". */
  readonly oQue: string;
  readonly children: ReactNode;
  readonly className?: string;
}) {
  return (
    <div role="status" aria-busy="true" className={className}>
      <span className="sr-only">{oQue}</span>
      {children}
    </div>
  );
}

/* Larguras que se alternam de linha para linha: texto de verdade nao termina
   sempre no mesmo ponto, e um bloco de barras iguais le como grade, nao como
   paragrafo. */
const LARGURAS_DE_LINHA = ['w-full', 'w-11/12', 'w-4/5', 'w-full', 'w-5/6'] as const;

/** Um paragrafo: a ultima linha mais curta, como a de um texto que acabou. */
export function EsqueletoTexto({
  oQue,
  linhas = 3,
  className = '',
}: {
  readonly oQue: string;
  readonly linhas?: number;
  readonly className?: string;
}) {
  return (
    <Carregando oQue={oQue} className={`flex flex-col gap-3 py-1 ${className}`}>
      {Array.from({ length: linhas }, (_, i) => (
        <Barra
          key={i}
          className={`h-3 ${i === linhas - 1 && linhas > 1 ? 'w-3/5' : LARGURAS_DE_LINHA[i % LARGURAS_DE_LINHA.length]}`}
        />
      ))}
    </Carregando>
  );
}

/**
 * Pares rotulo e valor (os dados-chave do painel de detalhe, p.28, e as fichas
 * em `dl`): rotulo curto em cima, valor embaixo, em grade de duas colunas.
 */
export function EsqueletoDeDados({
  oQue,
  pares = 6,
  colunas = 2,
}: {
  readonly oQue: string;
  readonly pares?: number;
  readonly colunas?: 1 | 2 | 3;
}) {
  const grade = colunas === 1 ? 'grid-cols-1' : colunas === 3 ? 'grid-cols-3' : 'grid-cols-2';
  return (
    <Carregando oQue={oQue} className={`grid ${grade} gap-x-4 gap-y-4`}>
      {Array.from({ length: pares }, (_, i) => (
        <div key={i} className="flex min-w-0 flex-col gap-2">
          <Barra className="h-2 w-16" />
          <Barra className={`h-3 ${i % 3 === 1 ? 'w-1/2' : 'w-3/4'}`} />
        </div>
      ))}
    </Carregando>
  );
}

/** Um campo (p.22): o rotulo em cima e o controle de 40 px, raio 12. */
export function EsqueletoDeCampo({ className = '' }: { readonly className?: string }) {
  return (
    <span aria-hidden="true" className={`flex min-w-0 flex-col gap-2 ${className}`}>
      <Barra className="h-3 w-24" />
      <Barra className="h-[var(--controle-m)] w-full rounded-controle" />
    </span>
  );
}

/**
 * O CONTROLE de um campo cujas opcoes ainda estao vindo — a lista de obras, de
 * pessoas, de tanques. O rotulo do `Campo` fica (ele ja se sabe); so o
 * controle vira a barra de 40 px, e quem le a tela ouve o que esta vindo. Uma
 * lista de selecao vazia, ou com "Carregando…" escrito como opcao, deixa
 * escolher o nada.
 */
export function ControleCarregando({ oQue }: { readonly oQue: string }) {
  return (
    <span role="status" aria-busy="true" className="block">
      <span className="sr-only">{oQue}</span>
      <Barra className="h-[var(--controle-m)] w-full rounded-controle" />
    </span>
  );
}

/**
 * Uma lista de itens com icone e duas linhas — avisos, documentos, anexos,
 * historico. A divisoria e a mesma da lista de verdade.
 */
export function EsqueletoDeLista({
  oQue,
  itens = 4,
  icone = true,
  className = '',
}: {
  readonly oQue: string;
  readonly itens?: number;
  /** O bloco de 36 px a esquerda, quando a lista de verdade tem. */
  readonly icone?: boolean;
  readonly className?: string;
}) {
  return (
    <Carregando oQue={oQue} className={`flex flex-col ${className}`}>
      {Array.from({ length: itens }, (_, i) => (
        <div key={i} className="flex gap-3 border-b border-borda-sutil py-3 last:border-b-0">
          {icone ? <Barra className="size-[var(--indicador-bloco)] rounded-controle" /> : null}
          <div className="flex min-w-0 flex-1 flex-col gap-2 pt-1">
            <Barra className={`h-3 ${i % 2 === 0 ? 'w-1/2' : 'w-2/5'}`} />
            <Barra className={`h-3 ${LARGURAS_DE_LINHA[(i + 1) % LARGURAS_DE_LINHA.length]}`} />
          </div>
        </div>
      ))}
    </Carregando>
  );
}

